import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Group, Stack } from '@mantine/core'
import SegmentControlUpdated from 'app/views/components/segments/segment-control-updated'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useParams, useMatch } from 'react-router-dom'
import { loadAllEntityProfiles } from 'app/state/ducks/entity-profile/thunks'
import TradeHeader from './trade-header'
import { MasterTradeParams } from 'app/models/master-trade-params'
import { getMasterTrades, getLockedMasterTrades } from 'app/state/ducks/master-trades/selectors'
import { loadCalendars } from 'app/state/ducks/holiday-calendars/thunks'
import { getUnreadCounts } from 'app/state/ducks/trade-notes/selectors'
import { loadTradeNoteUnreadCounts } from 'app/state/ducks/trade-notes/thunks'
import { TradeSearch } from '../trade-search'
import PrimaryInput from 'app/views/components/buttons/PrimaryInput'
import { useNavigate } from 'react-router-dom'
import { CustomLoaderPage } from 'app/views/components/loader/loader-page'
import TradeActions from './trade-detail-actions'
import { SelectedSubTradesProvider } from './selected-sub-trades-context'
import { DeliveryConfirmProvider } from './delivery-confirm-context'
import { getSubTrades } from 'app/state/ducks/sub-trades/selectors'
import { loadTradeDetail, loadTradeDocuments, loadTradeOverview } from 'app/state/ducks/trade-detail/thunks'
import { loadMasterTradeStream } from './activity-stream/activity-stream-daml-actions'
import { TRADE_DOCUMENTS_ROUTE, TRADE_OVERVIEW_ROUTE, TRADE_SUB_TRADE_ROUTE } from './use-trade-refresh'
import { loadTradeList } from 'app/state/ducks/trade-list/thunks'
import { getTradeList, getTradeListLoader } from 'app/state/ducks/trade-list/selectors'

export function DetailTradeDrillPage() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { tradeId } = useParams()
  const isDocumentsTab = !!useMatch(TRADE_DOCUMENTS_ROUTE)
  const isOverviewTab = !!useMatch(TRADE_OVERVIEW_ROUTE)
  const isSubTradeTab = !!useMatch(TRADE_SUB_TRADE_ROUTE)
  const allMasterTrades = useSelector(getMasterTrades)
  const lockedMasterTrades = useSelector(getLockedMasterTrades)
  const subTrades = useSelector(getSubTrades)
  const unreadCounts = useSelector(getUnreadCounts)
  const tradeList = useSelector(getTradeList)
  const tradeListLoading = useSelector(getTradeListLoader)

  const [trade, setTrade] = useState<MasterTradeParams | undefined>()
  // Which read has landed, for which trade: the Overview view, the Sub-trade
  // read, the Documents read, or the Activity Stream's (the Sub-trade read
  // without the lists only the Sub-trade tab needs).
  const read = isOverviewTab
    ? 'overview'
    : isSubTradeTab
      ? 'detail'
      : isDocumentsTab
        ? 'documents'
        : 'activity'
  const [loadedRead, setLoadedRead] = useState<string | undefined>()
  const isLoading = loadedRead !== `${read}:${tradeId}`

  // The open tab's read (ADR_LOS-030). Re-read when Trade Search navigates
  // to another trade — the page does not remount.
  //
  // - Overview: its own view — ONE request running one statement, returning
  //   what the tab and the header show; the master trade into the
  //   master-trade store, the rest into the tradeDetail slice. Plus the two
  //   small lists the page needs anyway (calendars for T+, the Activity
  //   Stream badge).
  // - Sub-trade: its own read — ONE request running one statement, the
  //   trade's records into the stores the tab, its expand panel and its
  //   editors read, its lookups into the tradeDetail slice. Plus calendars
  //   (T+, settlement dates), the Activity Stream badge and entity profiles
  //   (the fund-manager check, before the tab renders).
  // - Documents: its own read — ONE request running one statement: the
  //   Sub-trade read's records and lookups (the tab's rows, expand panel and
  //   bulk actions read the same), plus signable documents, funding-memo
  //   metadata and deliveries. Plus calendars and the Activity Stream badge.
  // - Activity Stream: its own read (LOS-7095) — ONE request running one
  //   statement: the trade into the master-trade store (the header, Delete
  //   Draft), the header's warnings and the note composer's sub trades into
  //   the tradeDetail slice, the notes into the trade-notes store — with the
  //   stream's failure toast, so a tab that cannot load never shows an empty
  //   stream silently. Plus the tab's badge. No tab loads the tenant-wide
  //   lists any more.
  useEffect(() => {
    if (!tradeId) return
    let cancelled = false
    const key = `${read}:${tradeId}`
    setLoadedRead(undefined)
    const thunks: (() => any)[] = read === 'overview'
      ? [() => loadTradeOverview(tradeId), loadCalendars, loadTradeNoteUnreadCounts]
      : read === 'detail'
      ? [() => loadTradeDetail(tradeId), loadCalendars, loadTradeNoteUnreadCounts, loadAllEntityProfiles]
      : read === 'documents'
      ? [() => loadTradeDocuments(tradeId), loadCalendars, loadTradeNoteUnreadCounts]
      : [() => (d: any) => loadMasterTradeStream(tradeId, d), loadTradeNoteUnreadCounts]

    const loadData = async () => {
      try {
        await Promise.all(thunks.map((thunk) => dispatch(thunk())))
      } finally {
        if (!cancelled) {
          setLoadedRead(key)
        }
      }
    }

    loadData()
    return () => {
      cancelled = true
    }
  }, [dispatch, tradeId, read])

  // Trade Search matches every trade in the tenant, which the per-trade
  // reads do not load: it reads the Trade List projection —
  // arriving from the list it is already in the store; on a deep link it is
  // fetched the first time someone types.
  const loadTradeListOnce = useCallback(() => {
    if (tradeList.length === 0) dispatch(loadTradeList() as any)
  }, [dispatch, tradeList.length])

  useEffect(() => {
    if (tradeId) {
      // The master-trade store holds this trade from the open tab's read, in
      // whichever state it is (locked included).
      let masterTrade = allMasterTrades.find(trade => trade.id === tradeId)
      if (!masterTrade) {
        masterTrade = lockedMasterTrades.find(trade => trade.id === tradeId)
      }
      setTrade(masterTrade)
    }
  }, [tradeId, allMasterTrades, lockedMasterTrades])

  const subTradeIdsForTrade = (subTrades ?? [])
    .filter(st => st?.masterTradeId?.id === trade?.id)
    .map(st => st.id)
    .filter(Boolean) as string[]

  const tabs = {
    overview: { route: `/tradeentry/${tradeId}`, label: 'OVERVIEW' },
    subTrade: { route: `/tradeentry/${tradeId}/subtrade`, label: 'SUB-TRADE' },
    documents: { route: `/tradeentry/${tradeId}/documents`, label: 'DOCUMENTS' },
    // ADR_LOS-028 + read-state design: the badge counts portal-authored notes with no read
    // receipt across this master trade's sub-trades. Clicking notes in the tab refetches the
    // counts (mark-read thunk), so the badge decrements live.
    activity: {
      route: `/tradeentry/${tradeId}/activity`,
      label: 'ACTIVITY STREAM',
      badge: unreadCounts.byMasterTrade[tradeId ?? ''] ?? 0,
    },
  }

  const allTradesForSearch = useMemo(
    () =>
      tradeList.map(row => ({
        id: row.id,
        systemId: row.tradeId,
        clientId: row.internalId ?? undefined,
        status: row.status,
      })) as MasterTradeParams[],
    [tradeList],
  )

  return (
    <Stack w="100%">
      <TradeHeader trade={trade} />
      <Group w="100%" position="apart" className='tabsGapsActivityWithTradeSearch'>
        <Group position="right">
          <SegmentControlUpdated tabs={tabs} loading={isLoading} />
        </Group>
        <Group noWrap className="searchActionsGroup" align="center">
          <TradeSearch
            data={allTradesForSearch}
            placeholder="Trade Search"
            onSearchStart={loadTradeListOnce}
            loading={!!tradeListLoading}
          />
          <PrimaryInput
            onClick={() => {
              navigate('/tradeentry/create', { state: { tab: 'tradeEntryCreation' } });
            }}
            text="Create Trade"
          />
        </Group>
      </Group>
      <SelectedSubTradesProvider>
        <DeliveryConfirmProvider masterTradeId={trade?.id ?? ''} subTradeIds={subTradeIdsForTrade}>
          {/* On the Documents tab the bulk-action control is rendered inside the
              tab's toolbar (left of the Status filter, LOS-5802). Elsewhere every
              menu item except Delete Draft is gated to the Documents tab, so the
              button is only mounted when Delete Draft can actually appear — a
              non-draft trade would otherwise get an Actions button opening an
              empty dropdown. */}
          {!isDocumentsTab && trade?.status === 'Draft' && <TradeActions row={trade} />}
          {isLoading ? <CustomLoaderPage /> : <Outlet />}
        </DeliveryConfirmProvider>
      </SelectedSubTradesProvider>
    </Stack>
  )
}
